import { StandardEvents } from '@shopify/events';

/**
 * Golden Hour: no size is chosen until she chooses one.
 *
 * The page arrives with no size ticked, Add to cart held and the line
 * "Choose your size" above it (server side: snippets/ghw-size-pending and
 * its hooks in Horizon's variant picker, buy buttons and sticky bar; see
 * CUSTOMIZATIONS.md). This script keeps it that way until she taps a size.
 *
 * Why it is needed at all: Horizon asks the server for a fresh picker on
 * every colour change, with the option values that are ticked. With only a
 * colour ticked the server fills in a size itself, the first one, and the
 * picker, the form's variant id, the button and the sticky bar all come
 * back as if that size had been chosen. So after every such change,
 * while no size has been chosen, this puts it back:
 *
 * - every size radio unticked;
 * - the form's variant id empty, so nothing can be posted, not even by a
 *   script that does not know about this;
 * - Add to cart and the sticky bar's button held (aria-disabled, never
 *   disabled), in their own style; a tap on either, or on the second Add
 *   to cart, scrolls to the sizes and lights them up instead;
 * - no ?variant= in the address (Horizon's variant-picker.js checks the
 *   flag on <html> before writing one; this also takes out a late one).
 *
 * The state lives on <html data-ghw-size-pending>, because Horizon morphs
 * the picker and the form and would wipe it from either. It ends the
 * moment a size radio changes, by her hand or by the pack chooser setting
 * the picker for its first pair. A change of colour never ends it: the
 * colour tiles further down the page tick a colour and nothing else.
 */

const ROOT = document.documentElement;
const SIZE = /size|maat/i;

const script = document.querySelector('script[data-ghw-size-pending-script]');
const priceFrom = script?.getAttribute('data-price-from') || '';

let pending = true;

const isSizeInput = (el) =>
  el instanceof HTMLInputElement && el.type === 'radio' && !!el.closest('variant-picker') && SIZE.test(el.dataset.optionName || '');

/** Only the product's own picker, not a quick-add card elsewhere. */
function picker() {
  return document.querySelector('variant-picker[data-template-product-match="true"]') ?? document.querySelector('.product-information variant-picker');
}

function form() {
  return picker()?.closest('.shopify-section')?.querySelector('product-form-component') ?? document.querySelector('product-form-component');
}

function sizeRadios() {
  return [...(picker()?.querySelectorAll('input[type="radio"]') ?? [])].filter(isSizeInput);
}

function note() {
  return document.querySelector('[data-ghw-size-note]');
}

function stripVariantFromUrl() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('variant')) return;
  url.searchParams.delete('variant');
  history.replaceState(history.state, '', url.toString());
}

/** Put the held state back on everything a re-render may have undone. */
function apply() {
  if (!pending) return;
  ROOT.setAttribute('data-ghw-size-pending', '');

  for (const radio of sizeRadios()) {
    radio.checked = false;
    radio.removeAttribute('checked');
    radio.dataset.currentChecked = 'false';
    radio.dataset.previousChecked = 'false';
  }

  const productForm = form();
  const idInput = productForm?.querySelector('input[name="id"]');
  if (idInput) idInput.value = '';

  // Held, not disabled. A disabled button takes no tap at all, and a tap
  // that does nothing reads as a broken shop: she leaves. aria-disabled
  // keeps it tappable and reachable by keyboard; the guard below turns the
  // tap into "here are the sizes". The server renders it disabled, so it
  // is safe before this script has run, and it is let go of here.
  const button = productForm?.querySelector('button[name="add"], [ref="addToCartButton"]');
  const stickyButton = document.querySelector('sticky-add-to-cart [ref="addToCartButton"]');
  for (const el of [button, stickyButton]) {
    if (!el) continue;
    el.disabled = false;
    el.setAttribute('aria-disabled', 'true');
    el.setAttribute('data-ghw-size-pending', '');
    el.setAttribute('aria-describedby', 'ghw-size-note');
  }
  const sticky = document.querySelector('sticky-add-to-cart');
  const stickyVariant = sticky?.querySelector('.sticky-add-to-cart__variant');
  const text = note()?.querySelector('[data-ghw-size-note-text]')?.textContent?.trim() || 'Choose your size';
  if (stickyVariant) stickyVariant.textContent = text;

  if (priceFrom) {
    for (const price of productForm?.closest('.shopify-section')?.querySelectorAll('[ref="priceContainer"] .price') ?? []) {
      price.textContent = `From ${priceFrom}`;
    }
  }

  const line = note();
  if (line) line.hidden = packTakesOver;

  stripVariantFromUrl();
}

/** She chose a size: everything behaves as Horizon always does again. */
function release() {
  if (!pending) return;
  pending = false;
  ROOT.removeAttribute('data-ghw-size-pending');
  const line = note();
  if (line) line.hidden = true;
  for (const el of document.querySelectorAll('[data-ghw-size-pending]')) {
    el.removeAttribute('data-ghw-size-pending');
    // Only take back what this script put there; a pack still waiting for
    // its rows keeps its own hold (ghw-bundle).
    if (!el.hasAttribute('data-ghw-bundle-held')) el.removeAttribute('aria-disabled');
    if (el.getAttribute('aria-describedby') === 'ghw-size-note') el.removeAttribute('aria-describedby');
  }
  note()?.classList.remove('ghw-size-note--called');
  for (const fieldset of document.querySelectorAll('[data-ghw-size-called]')) fieldset.removeAttribute('data-ghw-size-called');
  document.dispatchEvent(new CustomEvent('ghw:size-pending', { detail: { pending: false } }));
}

/**
 * Take her to the sizes: scroll there, light them up for a moment, put the
 * keyboard focus on the first size, and say "Choose your size" again so a
 * screen reader reads it out. Nothing goes in the cart.
 */
let calledTimer = 0;
function callSizes() {
  const fieldset = sizeRadios()[0]?.closest('fieldset');
  if (!fieldset) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  fieldset.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
  fieldset.setAttribute('data-ghw-size-called', '');
  window.clearTimeout(calledTimer);
  calledTimer = window.setTimeout(() => fieldset.removeAttribute('data-ghw-size-called'), 2500);
  sizeRadios()[0]?.focus({ preventScroll: true });

  const line = note();
  if (line) {
    line.hidden = false;
    line.classList.add('ghw-size-note--called');
    const text = line.querySelector('[data-ghw-size-note-text]');
    if (text) {
      // A status region only speaks when its text changes.
      const words = text.textContent;
      text.textContent = '';
      window.setTimeout(() => (text.textContent = words), 60);
    }
  }
}

// On the pack chooser's own mode (Sianna) a pack of two or more has a row
// per pair, each with its own "Choose a size" line. The line above the
// button would then say the same thing a second time, so it steps back.
let packTakesOver = false;
document.addEventListener('ghw:bundle', (event) => {
  const bundle = document.querySelector('ghw-bundle-component[data-first-row="own"]');
  packTakesOver = !!bundle && (event.detail?.count ?? 1) > 1;
  if (pending) {
    const line = note();
    if (line) line.hidden = packTakesOver;
  }
});

// A size radio changing is the one way out. Capture phase, so the flag is
// gone before Horizon's own change handler decides about the address.
document.addEventListener(
  'change',
  (event) => {
    if (pending && isSizeInput(event.target)) release();
  },
  { capture: true }
);

// After every re-render of the picker, while still pending, put it back.
// The wait for a frame lets Horizon's own listeners (form, sticky bar,
// price) finish with the new HTML first; nothing is painted in between.
document.addEventListener(StandardEvents.productSelect, async (event) => {
  if (!pending) return;
  try {
    await event.promise;
  } catch {
    /* aborted by a newer change; that one will land here too */
  }
  await new Promise((resolve) => requestAnimationFrame(resolve));
  apply();
  document.dispatchEvent(new CustomEvent('ghw:size-pending', { detail: { pending } }));
  // Horizon writes ?variant= a moment after the click; if a slow device
  // wrote it after the check above, take it out once more.
  window.setTimeout(() => pending && stripVariantFromUrl(), 300);
});

// The guard. On window, in the capture phase, so it runs before anything
// else on the page sees the tap: before Horizon's product form submits,
// and before the sticky bar plays its fly-to-cart animation for an add
// that is not going to happen. It also catches the click that the Enter
// key produces on a focused button.
window.addEventListener(
  'click',
  (event) => {
    if (!pending || !(event.target instanceof Element)) return;
    const hit = event.target.closest(
      'product-form-component button[name="add"], sticky-add-to-cart [ref="addToCartButton"], .ghw-again__button'
    );
    if (!hit) return;
    // A pack of two or more on Sianna has its own rows to fill in; the
    // pack chooser's guard takes those taps (ghw-bundle.js).
    if (packTakesOver) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    callSizes();
  },
  { capture: true }
);

// A page restored from the back/forward cache comes back as it was left.
window.addEventListener('pageshow', () => pending && apply());

if (ROOT.hasAttribute('data-ghw-size-pending') || document.querySelector('[data-ghw-size-note]')) {
  apply();
  document.dispatchEvent(new CustomEvent('ghw:size-pending', { detail: { pending: true } }));
}
